// SMOOTH CUTAWAY GHOSTS (owner 2026-11-02: "weird graphical glitches when I walk past certain assets of the map").
// The occlusion cutaway (props.ts: trees / spires / bush tops between the camera and the hero, edgeWall.ts: the near arena
// wall) used to discard a screen-door share of the pixels. On a phone at quality low (+ dynamic resolution) the scene is drawn
// at ~0.45–0.9 px per CSS px, so that 1-px dither was upscaled into a coarse, noisy checkerboard.
//
// Now a prop that might be cut is drawn by a small "ghost twin" InstancedMesh instead of its opaque batch: same geometry, a
// copy of the material made truly transparent (alpha blended), drawn after the other transparent effects (they show through
// it like anything else behind it). A depth-only pre-pass of the same ghost goes first, so only its nearest surface is
// blended: no self-overlap (a double-sided canopy would otherwise show its dark undersides through itself) and no sorting
// noise. Works the same on WebGPU and WebGL2 (no MSAA / alpha-to-coverage needed). The per-fragment cut shape is unchanged
// (the spatial S, 0 = keep, 1 = fully cut); the alpha is 1 - depth * S, where `depth` is a per-instance value the CPU eases
// in / out over GHOST_EASE so nothing pops. The opaque batches lose their mask (early-z is back for every solid prop) and
// only the handful of instances near the hero pay for the extra passes.
//
// Ink hulls (cel outlines, back-face shells): each ghost twin has an ink twin that fades with the body; the depth pre-pass
// hides the hull's inside exactly as an opaque body does.
//
// The CPU side only has to be roughly right: a prop wrongly left in its opaque batch simply is not faded (never a hole), and a
// prop wrongly sent to the ghost twin is drawn with alpha 1 wherever S = 0.
import * as THREE from 'three/webgpu';
import { float, instancedDynamicBufferAttribute, materialOpacity } from 'three/tsl';

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

/** seconds for a ghost to fade fully in / out */
export const GHOST_EASE = 0.15;

/** Per-instance fade depth (0 = opaque … 1 - minAlpha) shared by a body twin and its ink twin. */
export class GhostDepth {
  readonly arr: Float32Array;
  readonly attr: THREE.InstancedBufferAttribute;
  readonly node: N;
  constructor(readonly cap: number) {
    this.arr = new Float32Array(cap);
    this.attr = new THREE.InstancedBufferAttribute(this.arr, 1);
    this.attr.setUsage(THREE.DynamicDrawUsage);
    this.node = instancedDynamicBufferAttribute(this.attr, 'float');
  }
}

/** copy the node slots a plain clone() can miss on custom materials (same list as the edge wall used) */
function cloneNodeMat(src: THREE.Material): THREE.NodeMaterial {
  const s = src as THREE.NodeMaterial & { emissiveNode?: unknown; gradientMap?: unknown };
  const m = s.clone() as typeof s;
  m.colorNode = s.colorNode; m.emissiveNode = s.emissiveNode; m.normalNode = s.normalNode; m.positionNode = s.positionNode;
  m.opacityNode = s.opacityNode; if ('gradientMap' in s) m.gradientMap = s.gradientMap; m.vertexColors = s.vertexColors;
  m.maskShadowNode = s.maskShadowNode;
  return m;
}

/** Transparent ghost copy of a prop / wall material. S = spatial cut 0..1 (per fragment), depth = per-instance fade depth. */
export function ghostBodyMaterial(src: THREE.Material, S: N, depth: N): THREE.NodeMaterial {
  const s = src as THREE.NodeMaterial;
  const m = cloneNodeMat(src);
  const alpha = float(1).sub(float(depth).mul(S));
  if (s.alphaTest > 0) {
    // cut-out cards (painted leaves): keep the card shape as a hard mask, the ghost alpha is the fade alone
    const shape = float((s.opacityNode ?? materialOpacity) as N).greaterThan(s.alphaTest);
    m.maskNode = s.maskNode ? (s.maskNode as N).and(shape) : shape;
    m.alphaTest = 0;
  } else m.maskNode = s.maskNode;
  m.opacityNode = alpha;
  m.transparent = true; m.depthWrite = false; m.forceSinglePass = true;
  return m;
}

/** Ink hull of a ghost: fades with the body (the depth pre-pass keeps the hull's inside hidden, as for a solid prop). */
export function ghostInkMaterial(src: THREE.Material, S: N, depth: N): THREE.NodeMaterial {
  const m = cloneNodeMat(src);
  m.maskNode = null;
  const a = float(1).sub(float(depth).mul(S));
  m.opacityNode = a.mul(a);
  m.transparent = true; m.depthWrite = false; m.forceSinglePass = true;
  return m;
}

/** Draw order: ghosts come last in the transparent list (after every effect, which then shows through them like anything
 *  else behind a see-through prop). Per mesh part p: depth pre-pass at BASE + 2p, colour at BASE + 2p + 1, ink after all. */
export const GHOST_ORDER = 100, GHOST_INK_ORDER = 140;

/**
 * A ghost twin for one InstancedMesh (part index `part` of its set): own small instance buffers, same geometry. A child
 * mesh lays down the ghost's depth first (colour writes off), so the transparent pass blends only the nearest surface —
 * without it, a double-sided canopy shows its own dark undersides and inner lobes through itself.
 */
export function ghostTwin(src: THREE.InstancedMesh, mat: THREE.NodeMaterial, cap: number, colors: boolean, part = 0): THREE.InstancedMesh {
  const g = new THREE.InstancedMesh(src.geometry, mat, cap);
  if (colors) g.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
  g.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  g.castShadow = src.castShadow; g.receiveShadow = src.receiveShadow; g.frustumCulled = false; g.count = 0;
  g.renderOrder = GHOST_ORDER + 2 * part + 1; g.name = 'cut-ghost';
  const pm = cloneNodeMat(mat); pm.maskNode = mat.maskNode; pm.opacityNode = mat.opacityNode;
  pm.transparent = true; pm.depthWrite = true; pm.colorWrite = false; pm.forceSinglePass = true;
  const pre = new THREE.InstancedMesh(src.geometry, pm, cap);
  pre.instanceMatrix = g.instanceMatrix; if (g.instanceColor) pre.instanceColor = g.instanceColor;
  pre.castShadow = false; pre.receiveShadow = false; pre.frustumCulled = false; pre.count = 0;
  pre.renderOrder = GHOST_ORDER + 2 * part; pre.name = 'cut-ghost-depth';
  g.add(pre);
  return g;
}

/** A single always-ghostable mesh (a few large set pieces, e.g. Rust Canyon's arches): drawn in the ghost slots of the
 *  transparent list with its own depth pre-pass, so it looks exactly opaque wherever its alpha is 1. */
export function ghostMesh(geo: THREE.BufferGeometry, mat: THREE.NodeMaterial, part = 0): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat); m.renderOrder = GHOST_ORDER + 2 * part + 1;
  const pm = cloneNodeMat(mat); pm.maskNode = mat.maskNode; pm.opacityNode = mat.opacityNode;
  pm.transparent = true; pm.depthWrite = true; pm.colorWrite = false; pm.forceSinglePass = true;
  const pre = new THREE.Mesh(geo, pm); pre.renderOrder = GHOST_ORDER + 2 * part; pre.castShadow = false; pre.receiveShadow = false;
  m.add(pre);
  return m;
}

/** Set how many ghosts a twin (and its depth pre-pass) draws. */
export function setGhostCount(g: THREE.InstancedMesh, n: number): void {
  g.count = n; g.visible = n > 0;
  for (const c of g.children) (c as THREE.InstancedMesh).count = n;
  if (n) g.instanceMatrix.needsUpdate = true;
}

/** Ink twin of a ghost twin (shares its instance matrices). */
export function ghostInkTwin(body: THREE.InstancedMesh, outlineGeo: THREE.BufferGeometry, mat: THREE.Material, cap: number): THREE.InstancedMesh {
  const o = new THREE.InstancedMesh(outlineGeo, mat, cap);
  o.instanceMatrix = body.instanceMatrix; o.frustumCulled = false; o.count = 0; o.castShadow = false; o.receiveShadow = false;
  o.renderOrder = GHOST_INK_ORDER; o.name = 'cut-ghost-ink';
  return o;
}

// ------------------------------------------------------------------------------------------------ CPU candidate tests

/** Could a vertical cylinder (centre x/z, radius R, heights y0..y1) come within `reach` of the segment from the hero's chest
 *  (focus + 1 m) to the camera? Sampled along the segment (the reach includes the sampling slack). */
export function nearHeroLine(cam: THREE.Vector3, F: THREE.Vector3, x: number, z: number, R: number, y0: number, y1: number, reach: number): boolean {
  const ax = F.x, ay = F.y + 1, az = F.z, bx = cam.x - ax, by = cam.y - ay, bz = cam.z - az;
  const r2 = reach * reach;
  for (let i = 0; i <= 16; i++) {
    const t = i / 16, lx = ax + bx * t, ly = ay + by * t, lz = az + bz * t;
    const h = Math.max(0, Math.hypot(lx - x, lz - z) - R), v = ly > y1 ? ly - y1 : ly < y0 ? y0 - ly : 0;
    if (h * h + v * v < r2) return true;
  }
  return false;
}

/** Bounding box (world x/z) of the ground projection — onto the plane y = F.y, along rays from the camera, exactly as the
 *  shaders do — of the part of a vertical cylinder between heights ya..yb. False when that part is empty. Heights close to the
 *  camera's are clamped (they project far beyond the play area). */
export function projectedBox(cam: THREE.Vector3, Fy: number, x: number, z: number, R: number, ya: number, yb: number, out: Float32Array): boolean {
  const top = Fy + 0.85 * (cam.y - Fy);
  if (yb > top) yb = top;
  if (yb < ya) return false;
  out[0] = out[2] = Infinity; out[1] = out[3] = -Infinity;
  for (const y of [ya, yb]) {
    const k = (cam.y - Fy) / Math.max(0.01, cam.y - y);
    const cx = cam.x + (x - cam.x) * k, cz = cam.z + (z - cam.z) * k, r = R * k;
    out[0] = Math.min(out[0], cx - r); out[1] = Math.max(out[1], cx + r);
    out[2] = Math.min(out[2], cz - r); out[3] = Math.max(out[3], cz + r);
  }
  return true;
}

/** Linear approach of an eased value toward 0 / 1 (GHOST_EASE seconds end to end). */
export const easeStep = (a: number, on: boolean, dt: number): number => on ? Math.min(1, a + dt / GHOST_EASE) : Math.max(0, a - dt / GHOST_EASE);
/** smoothstep shape of the eased value (soft start and end) */
export const easeShape = (a: number): number => a * a * (3 - 2 * a);
